/**
 * DECIDE — the decision inbox, the findings backlog, run history, and the two
 * detail pages that carry the product's weight: a finding (where a human decides)
 * and a run (where the whole chain from claim to receipt is legible).
 */

import { useMemo, useState } from "react";
import { clusterFindings } from "@excellent/core/verification-finding-clusters-client";
import { draftRationale, signRationale } from "@excellent/core/verification-rationale-drafts";
import {
  ACTORS,
  AUTHORITY_LABEL,
  CASE_STATUS_LABEL,
  CASE_STATUS_TONE,
  DECISION_HELP,
  DECISION_LABEL,
  JUDGMENTS,
  RUNS,
  SEMANTIC_ROUTE_LABEL,
  actor,
  artifact,
  checksFor,
  control,
  fmtDateTime,
  fmtNumber,
  fmtRelative,
  finding as findFinding,
  policy,
  receipt,
  requirementsFor,
  run as findRun,
  source,
  type Consequence,
  type DecisionEvent,
  type Finding,
  type ReviewDecisionKind,
  type VerificationStatus,
} from "./data";
import { href } from "./router";
import {
  Badge,
  Button,
  Callout,
  Card,
  Choice,
  Code,
  Empty,
  MissingObjectPage,
  Field,
  FieldGroup,
  Fields,
  Hash,
  Label,
  Meter,
  PageShell,
  Section,
  Segmented,
  SearchBox,
  Select,
  Severity,
  Table,
  TextArea,
  Timeline,
  TimelineItem,
  Verdict,
} from "./ui";
import type { Column } from "./ui";
import { myQueue, openFindings, useStore, worstFirst } from "./store";

/* ══════════════════════════════════════════════════════════════════════════
   DECISION INBOX
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * The decision inbox.
 *
 * ── Two design decisions worth defending ──
 *
 * 1. **Cards, not a table.** The findings backlog (next page) is a table, because
 *    there you are scanning 20+ rows for a pattern. Here you are doing one job at
 *    a time, and the thing that makes a decision fast is the ASK — one sentence
 *    saying what you are being asked to settle. That does not fit in a table cell,
 *    and burying it behind a click is what makes decision inboxes feel like
 *    data entry. Same objects, different job, different form.
 *
 * 2. **Worst-first, not newest-first.** Sorted by severity then age. A queue that
 *    defaults to newest-first is an activity feed, and it systematically buries the
 *    critical item that has been open longest — the one thing a reviewer must never
 *    miss.
 */
export function QueuePage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [scope, setScope] = useState<"mine" | "all" | "done">("mine");

  const mine = worstFirst(myQueue(store.findings));
  const all = worstFirst(openFindings(store.findings));
  const done = store.findings.filter((f) => f.status !== "open");

  const list = scope === "mine" ? mine : scope === "all" ? all : done;

  return (
    <PageShell
      eyebrow="Decide"
        title="Decision inbox"
        lede={
          <>
            Findings waiting on a human decision, worst first. A finding is only ever a{" "}
            <strong>FAIL</strong> or an <strong>UNKNOWN</strong> — a passing check never asks you for
            anything.
          </>
        }
        actions={
          <Segmented
            value={scope}
            onChange={setScope}
            options={[
              { key: "mine", label: `Assigned to me (${mine.length})` },
              { key: "all", label: `All open (${all.length})` },
              { key: "done", label: `Decided (${done.length})` },
            ]}
          />
        }
    >

      {scope !== "done" ? (
        <Callout tone="neutral" icon="info">
          <strong>UNKNOWN is not a soft failure.</strong> It means the evidence cannot decide the question
          either way. The right response is usually to fix the evidence, not to judge the control — and
          accepting an UNKNOWN as passing is the one thing this product will not let you do silently.
        </Callout>
      ) : null}

      <div className="vp-stack" style={{ marginTop: "calc(var(--spacing) * 4.5)" }}>
        {list.length === 0 ? (
          <Card>
            <Empty title="Nothing waiting on you">
              Every finding assigned to you has a recorded decision. New findings appear here the moment a run
              produces a FAIL or an UNKNOWN.
            </Empty>
          </Card>
        ) : null}

        {list.map((f) => (
          <QueueCard key={f.id} finding={f} onNavigate={onNavigate} />
        ))}
      </div>
    </PageShell>
  );
}

function QueueCard({ finding, onNavigate }: { finding: Finding; onNavigate: (to: string) => void }) {
  const store = useStore();
  const c = control(finding.controlId);
  const justDecided = store.decidedThisSession.includes(finding.id);

  return (
    <Card
      title={
        <span className="vp-inline">
          <Verdict status={finding.verificationStatus} />
          <span>{finding.title}</span>
        </span>
      }
      subtitle={
        <>
          <span className="vp-mono">{finding.ref}</span> · {c?.ref} {c?.title} · opened{" "}
          {fmtRelative(finding.openedAt)}
          {finding.dueAt ? ` · due ${fmtRelative(finding.dueAt)}` : ""}
        </>
      }
      actions={
        <>
          <Severity level={finding.severity} />
          <Badge tone={CASE_STATUS_TONE[finding.caseStatus]}>{CASE_STATUS_LABEL[finding.caseStatus]}</Badge>
        </>
      }
      footer={
        <>
          <span>
            Owner {actor(finding.ownerActorId).name} · authority {AUTHORITY_LABEL[finding.authority]} ·{" "}
            {finding.evidenceArtifactIds.length} evidence artifact
            {finding.evidenceArtifactIds.length === 1 ? "" : "s"}
          </span>
          <span style={{ marginLeft: "auto" }}>
        <Button
              size="sm"
              variant="primary"
              icon="arrow-right"
              onClick={() => onNavigate(href("findings", finding.id))}
            >
              Open and decide
        </Button>
          </span>
        </>
      }
    >
      {justDecided ? (
        <div style={{ marginBottom: "calc(var(--spacing) * 3)" }}>
          <Badge tone="pass" glyph="✓">
            You decided this in this session
          </Badge>
        </div>
      ) : null}

      {/* The ask. This is the line that makes the queue scannable — it says what
          the reviewer is being asked to settle, in the reviewer's own terms, rather
          than restating the machine's finding. */}
      <p style={{ margin: "0 0 calc(var(--spacing) * 2.5)", fontSize: "var(--type-body-size)", color: "var(--ink-1)", lineHeight: "var(--type-prose-leading)" }}>
        <strong>You are being asked to:</strong> {finding.ask}
      </p>
      <p style={{ margin: "0", fontSize: "var(--type-body-size)", color: "var(--ink-3)", lineHeight: "var(--type-prose-leading)" }}>{finding.summary}</p>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FINDINGS BACKLOG
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * B5 — "work a 200-finding week with one reviewer" — rendered by the SHIPPED
 * ENGINE, not by a fixture that agrees with it.
 *
 * `clusterFindings` is the real `@excellent/core/verification-finding-clusters-client`
 * function, the same one `test-verification-review-clustering` and
 * `test-verification-review-bulk` gate. It groups a queue by CAUSE so one
 * reviewer can act on a cause rather than on N findings, which is the entire
 * claim B5 makes.
 *
 * ⚠️ HONEST ABOUT THE INPUT. The prototype's fixture has no tenant or connector
 * fields, so those are supplied as stated constants below. That means the causes
 * you see here are the CONTROL and STALENESS causes only — the connector-scope
 * cause cannot appear, because nothing in this fixture can express it. The
 * clustering is real; the input is a fixture, and it is labelled as one on the
 * page rather than in a comment only.
 */
const CLUSTER_TENANT = "tenant_prototype";
const CLUSTER_CONTEXT = { now: "2026-08-18T00:00:00.000Z", stalenessThresholdHours: 24 * 30 };


/**
 * B5's other half: a model DRAFTS, a human SIGNS, and the draft can never become
 * the decision on its own. Run against the shipped `signRationale`, which
 * `test-verification-rationale-drafts` gates.
 *
 * One variable per row. The draft, the text and the clock are identical; only
 * the thing the rule turns on changes — so each refusal names its own cause
 * rather than leaving a reader to guess which of several differences mattered.
 */
const RATIONALE_DRAFT = draftRationale({
  finding: { id: "fnd_demo" },
  evidence: [{ ref: "ev_1", observedAt: "2026-08-17T00:00:00.000Z" }],
  worker: "semantic-worker-1",
  text: "The export shows MFA enforced for all admin accounts.",
  now: "2026-08-17T12:00:00.000Z",
});

const RATIONALE_CASES = [
  {
    label: "human signs the draft",
    result: signRationale({
      draft: RATIONALE_DRAFT,
      editedText: "The export shows MFA enforced for all admin accounts. Verified against the 14 Aug pull.",
      actorId: "act_reviewer",
      now: "2026-08-18T00:00:00.000Z",
    }),
  },
  {
    label: "no human actor",
    result: signRationale({
      draft: RATIONALE_DRAFT,
      editedText: "Looks fine.",
      actorId: "",
      now: "2026-08-18T00:00:00.000Z",
    }),
  },
  {
    label: "a DECISION presented as a draft",
    result: signRationale({
      draft: { ...RATIONALE_DRAFT, authority: "decision" } as never,
      editedText: "Looks fine.",
      actorId: "act_reviewer",
      now: "2026-08-18T00:00:00.000Z",
    }),
  },
];

export function FindingsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const store = useStore();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<"open" | "all" | "closed">("open");
  const [sev, setSev] = useState<"all" | Consequence>("all");
  const [owner, setOwner] = useState<string>("all");

  const rows = useMemo(() => {
    let list = store.findings;
    if (status === "open") list = list.filter((f) => f.status === "open");
    if (status === "closed") list = list.filter((f) => f.status !== "open");
    if (sev !== "all") list = list.filter((f) => f.severity === sev);
    if (owner !== "all") list = list.filter((f) => f.ownerActorId === owner);
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter(
        (f) =>
          f.title.toLowerCase().includes(needle) ||
          f.ref.toLowerCase().includes(needle) ||
          f.summary.toLowerCase().includes(needle) ||
          (control(f.controlId)?.ref ?? "").toLowerCase().includes(needle),
      );
    }
    return worstFirst(list);
  }, [store.findings, status, sev, owner, q]);

  // Live call into the shipped engine, over exactly the rows on screen — so the
  // cluster count moves when the filters do, rather than describing some other
  // set the reader cannot see.
  const clusters = useMemo(
    () =>
      clusterFindings(
        rows.map((f) => ({
          id: f.id,
          tenantId: CLUSTER_TENANT,
          controlId: f.controlId,
          connectorId: null,
          connectorScopeSatisfied: true,
          collectedAt: f.openedAt,
          policyRequirementId: f.requirementId,
          ownerActorId: f.ownerActorId,
        })),
        CLUSTER_CONTEXT,
      ),
    [rows],
  );

  const columns: Column<Finding>[] = [
    {
      key: "ref",
      header: "Finding",
      primary: true,
      sortValue: (f) => f.ref,
      render: (f) => (
        <>
          <span className="vp-mono" style={{ color: "var(--ink-3)" }}>
            {f.ref}
          </span>
          <div style={{ marginTop: "calc(var(--spacing) * 0.5)" }}>{f.title}</div>
        </>
      ),
    },
    {
      key: "control",
      header: "Control",
      sortValue: (f) => control(f.controlId)?.ref ?? "",
      render: (f) => <span className="vp-mono">{control(f.controlId)?.ref}</span>,
    },
    {
      key: "verdict",
      header: "Result",
      sortValue: (f) => f.verificationStatus,
      render: (f) => <Verdict status={f.verificationStatus} />,
    },
    {
      key: "sev",
      header: "Severity",
      sortValue: (f) => ({ critical: 0, high: 1, medium: 2, low: 3 })[f.severity],
      render: (f) => <Severity level={f.severity} />,
    },
    {
      key: "case",
      header: "Case state",
      sortValue: (f) => f.caseStatus,
      render: (f) => <Badge tone={CASE_STATUS_TONE[f.caseStatus]}>{CASE_STATUS_LABEL[f.caseStatus]}</Badge>,
    },
    {
      key: "owner",
      header: "Owner",
      sortValue: (f) => actor(f.ownerActorId).name,
      render: (f) => actor(f.ownerActorId).name,
    },
    {
      key: "age",
      header: "Age",
      sortValue: (f) => new Date(f.openedAt).getTime(),
      render: (f) => <span className="vp-mono">{fmtRelative(f.openedAt)}</span>,
    },
  ];

  return (
    <PageShell
      wide
      eyebrow="Decide"
        title="Findings"
        lede="Every FAIL and UNKNOWN across the tenant. This is the backlog view — for the one-at-a-time working view, use the decision inbox."
    >

      {/* B5 — the shipped clustering engine, run live over the rows below. */}
      <Section
        title="Draft, then signature"
        note="signRationale from @excellent/core/verification-rationale-drafts — the rule that a model's draft is never itself the decision."
      >
        <Card>
          <ul>
            {RATIONALE_CASES.map((c) => (
              <li key={c.label}>
                <strong>{c.label}</strong> —{" "}
                {c.result.ok ? (
                  <>
                    <span className="vp-mono">decision</span> · signed by{" "}
                    <span className="vp-mono">{c.result.decision.actorId}</span> · edit distance{" "}
                    {c.result.decision.editDistance}
                  </>
                ) : (
                  <span className="vp-mono">REFUSED: {c.result.reason}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="vp-row-sub">
            The worker's citations survive the signature — the human edits the words, not the
            evidence they point at. If the first row ever stops being the only one that succeeds,
            a model has become able to close a finding by itself.
          </p>
        </Card>
      </Section>

      <Section
        title="Causes"
        note={`${clusters.length} cause${clusters.length === 1 ? "" : "s"} across ${rows.length} finding${rows.length === 1 ? "" : "s"} — grouped by @excellent/core/verification-finding-clusters-client, the same clustering function used by the review-clustering gate without importing the server-only bulk-decision path.`}
      >
        <Card>
          {clusters.length === 0 ? (
            <p className="vp-row-sub">No findings match the current filters, so there is nothing to group.</p>
          ) : (
            <ul>
              {clusters.map((c) => (
                <li key={c.id}>
                  {/* The engine's own sentence, verbatim. Re-wording it here would
                      make the page and the gate disagree about what a cause IS. */}
                  <strong>{c.causeStatement}</strong>{" "}
                  <span className="vp-mono">{c.cause}</span> ·{" "}
                  {c.findingIds.length} finding{c.findingIds.length === 1 ? "" : "s"}
                  {/* sharedFix is the whole B5 proposition: ONE action closes the
                      cluster. Showing the cause without it would describe the
                      grouping and omit the reason grouping is worth doing. */}
                  <div className="vp-row-sub">Shared fix: {c.sharedFix}</div>
                </li>
              ))}
            </ul>
          )}
          <p className="vp-row-sub">
            The grouping is real; the INPUT is this prototype's fixture, which carries no tenant or
            connector fields. So only the control and staleness causes can appear here — a
            connector-scope cause is unreachable because nothing in the fixture can express one.
          </p>
        </Card>
      </Section>

      <div className="vp-filters">
        <SearchBox value={q} onChange={setQ} placeholder="Search findings, controls, summaries…" />
        <Select
          label="Status"
          value={status}
          onChange={setStatus}
          options={[
            { value: "open", label: "Open" },
            { value: "closed", label: "Decided" },
            { value: "all", label: "All statuses" },
          ]}
        />
        <Select
          label="Severity"
          value={sev}
          onChange={setSev}
          options={[
            { value: "all", label: "All severities" },
            { value: "critical", label: "Critical" },
            { value: "high", label: "High" },
            { value: "medium", label: "Medium" },
            { value: "low", label: "Low" },
          ]}
        />
        <Select
          label="Owner"
          value={owner}
          onChange={setOwner}
          options={[
            { value: "all", label: "All owners" },
            ...ACTORS.filter((a) => a.role !== "system").map((a) => ({ value: a.id, label: a.name })),
          ]}
        />
        <span className="vp-filter-spacer" />
        <span className="vp-result-count">
          {rows.length} of {store.findings.length}
        </span>
      </div>

      <Card flush>
        <Table
          columns={columns}
          rows={rows}
          rowKey={(f) => f.id}
          onRowClick={(f) => onNavigate(href("findings", f.id))}
          empty={
            <Empty title="No findings match these filters">
              Try clearing the search, or widen the status filter to include decided findings.
            </Empty>
          }
        />
      </Card>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   FINDING DETAIL — the decision surface
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A finding, and the decision about it.
 *
 * ── The layout argument ──
 *
 * Three things must be on screen simultaneously for a decision to be defensible:
 * what the engine found, what evidence it found it in, and what has already been
 * said about it. Put any of them behind a tab and the reviewer decides without it.
 * So: the finding and the decision form in the main column, the evidence and the
 * check that produced it in a sticky gutter, and the full decision trail beneath.
 *
 * ── The form is opinionated on purpose ──
 *
 * Five decisions, each with a plain-language consequence printed next to it,
 * because the difference between "accept as exception" and "dismiss" is the
 * difference between a stated limitation on an auditor's pack and a claim that
 * the finding was never valid. That distinction is invisible if the options are a
 * dropdown of verbs. The rationale box is required and says why.
 */
export function FindingDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const finding = store.findings.find((f) => f.id === id || f.ref === id) ?? findFinding(id);

  if (!finding) {
    return (
      <MissingObjectPage title={`No finding ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("queue"))}>
          Back to the inbox
        </Button>
      </MissingObjectPage>
    );
  }

  const live = store.findings.find((f) => f.id === finding.id) ?? finding;
  const c = control(live.controlId);
  const check = checksFor(live.runId).find((k) => k.id === live.checkId);
  const judgment = JUDGMENTS.find((j) => j.checkId === live.checkId);
  const pol = c ? policy(check?.policyId ?? "") : undefined;
  const req = requirementsFor(live.controlId).find((r) => r.id === live.requirementId);
  const closed = live.status !== "open";

  return (
    <PageShell
      wide
      eyebrow={`${c?.ref} · ${c?.family}`}
        title={live.title}
        lede={live.summary}
        actions={
          <>
        <Button
              icon="refresh"
              onClick={() => store.reverify(live.controlId)}
              title="Queue a fresh verification run for this control"
            >
              Re-verify
        </Button>
        <Button icon="arrow-right" onClick={() => onNavigate(href("runs", live.runId))}>
              Open the run
        </Button>
          </>
        }
    >

      <div className="vp-inline" style={{ marginBottom: "calc(var(--spacing) * 4.5)" }}>
        <Verdict status={live.verificationStatus} size="lg" />
        <Severity level={live.severity} />
        <Badge tone={CASE_STATUS_TONE[live.caseStatus]} size="lg">
          {CASE_STATUS_LABEL[live.caseStatus]}
        </Badge>
        <span className="vp-mono" style={{ color: "var(--ink-3)" }}>
          {live.ref}
        </span>
      </div>

      {live.verificationStatus === "UNKNOWN" ? (
        <Callout tone="unknown" icon="info" title="This is UNKNOWN, not a failure.">
          The engine could not decide this requirement from the evidence available. Recording it as a passing
          control would be asserting something the evidence does not show; recording it as a failure would be
          asserting something it also does not show. The productive move is almost always to fix the evidence.
        </Callout>
      ) : null}

      {judgment ? (
        <div style={{ marginTop: "calc(var(--spacing) * 3)" }}>
          <Callout tone="waived" icon="cpu" title="A model looked at this, and could not pass it.">
            The semantic worker returned <strong>{judgment.judgment}</strong> and routed this to{" "}
            <strong>{SEMANTIC_ROUTE_LABEL[judgment.route]}</strong>. Its authoritative status is{" "}
            <strong>{judgment.authoritativeStatus}</strong> — by construction, a model can never issue a PASS
            in this system. Its citations are below; they are evidence for your decision, not a substitute for
            it.
          </Callout>
        </div>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 5)" }}>
        <div className="vp-stack">
          {closed ? (
            <Card title="Decision recorded">
              <Fields>
                <Field label="Outcome">
                  <Badge tone={CASE_STATUS_TONE[live.caseStatus]} size="lg">
                    {CASE_STATUS_LABEL[live.caseStatus]}
                  </Badge>
                </Field>
                <Field label="Finding status">
                  <span className="vp-mono">{live.status}</span>
                </Field>
              </Fields>
              <div style={{ marginTop: "calc(var(--spacing) * 3)" }}>
                <Callout tone="neutral" icon="info">
                  A recorded decision is append-only. To change the outcome, record a new decision — the
                  original stays in the trail below, which is what makes the trail worth reading.
                </Callout>
              </div>
            </Card>
          ) : (
            <DecisionForm finding={live} />
          )}

          {/* ── The decision trail ─────────────────────────────────────── */}
          <Card
            title="Decision trail"
            subtitle="Append-only. Every entry names who acted, when, and why."
          >
            <Timeline>
              {live.timeline.map((e) => (
                <TimelineItem
                  key={e.id}
                  tone={EVENT_TONE[e.kind]}
                  glyph={EVENT_GLYPH[e.kind]}
                  title={e.title}
                  meta={`${actor(e.actorId).name} · ${fmtDateTime(e.at)} · ${fmtRelative(e.at)}`}
                  note={e.note}
                />
              ))}
            </Timeline>
          </Card>
        </div>

        {/* ── Gutter: the check, the evidence, the policy ─────────────── */}
        <aside className="vp-split-aside">
          <Card title="What the check found" flush>
            <div className="vp-card-body">
              {check ? (
                <>
                  <div className="vp-inline" style={{ marginBottom: "calc(var(--spacing) * 2.5)" }}>
                    <Verdict status={check.status} />
                    <Badge tone="neutral">{AUTHORITY_LABEL[check.authority]}</Badge>
                  </div>
                  <p style={{ margin: "0", fontSize: "var(--type-subtext-size)", lineHeight: "var(--type-subtext-leading)", color: "var(--ink-2)" }}>
                    {check.reason}
                  </p>
                </>
              ) : (
                <p className="vp-tiny">Check detail unavailable in this fixture.</p>
              )}
            </div>
            {req ? (
              <div className="vp-card-foot" style={{ display: "block" }}>
                <div style={{ fontWeight: 600, color: "var(--ink-1)", marginBottom: "calc(var(--spacing) * 1)" }}>
                  The requirement
                </div>
                {req.text}
              </div>
            ) : null}
          </Card>

          <Card
            title="Evidence"
            subtitle={`${live.evidenceArtifactIds.length} artifact${live.evidenceArtifactIds.length === 1 ? "" : "s"} · ${live.citationRefs.length} citation${live.citationRefs.length === 1 ? "" : "s"}`}
            flush
          >
            {live.evidenceArtifactIds.length === 0 ? (
              <div className="vp-card-body">
                <p className="vp-tiny" style={{ margin: "0" }}>
                  No evidence is mapped to this requirement at all. That is the finding.
                </p>
              </div>
            ) : (
              <div className="vp-rows">
                {live.evidenceArtifactIds.map((aid) => {
                  const art = artifact(aid);
                  if (!art) return null;
                  return (
                    <button
                      key={aid}
                      type="button"
                      className="vp-row"
                      data-clickable="true"
                      onClick={() => onNavigate(href("evidence", aid))}
                    >
                      <span className="vp-row-main">
                        <span className="vp-row-title">{art.label}</span>
                        <span className="vp-row-sub">
                          {source(art.sourceId)?.displayName.split(" —")[0]} · collected{" "}
                          {fmtRelative(art.collectedAt)} · {fmtNumber(art.observationCount)} observations
                        </span>
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {live.citationRefs.length > 0 ? (
              <div className="vp-card-foot" style={{ display: "block" }}>
                <div style={{ fontWeight: 600, color: "var(--ink-1)", marginBottom: "calc(var(--spacing) * 1)" }}>
                  Cited rows
                </div>
                {live.citationRefs.map((r) => (
                  <div key={r} className="vp-mono" style={{ fontSize: "var(--type-subtext-size)", marginBottom: "calc(var(--spacing) * 0.5)" }}>
                    {r}
                  </div>
                ))}
              </div>
            ) : null}
          </Card>

          {judgment ? (
            <Card title="Model citations" subtitle={`${judgment.promptVersion} · ${judgment.workerId}`}>
              <p style={{ margin: "0 0 calc(var(--spacing) * 2.5)", fontSize: "var(--type-subtext-size)", lineHeight: "var(--type-subtext-leading)", color: "var(--ink-2)" }}>
                {judgment.rationale}
              </p>
              {judgment.citations.map((cit, i) => (
                <div
                  key={i}
                  className="vp-tl-note"
                  style={{ marginTop: "calc(var(--spacing) * 1.5)", fontFamily: "var(--mono)", fontSize: "var(--type-subtext-size)" }}
                >
                  {cit.quote}
                </div>
              ))}
              {judgment.citations.length === 0 ? (
                <p className="vp-tiny" style={{ margin: "0" }}>
                  No citations — which is exactly why this routed to{" "}
                  <span className="vp-mono">unknown_uncited</span>.
                </p>
              ) : null}
            </Card>
          ) : null}

          {pol ? (
            <Card
              title="Active policy"
              subtitle={`${pol.id} · version ${pol.version}`}
              actions={
                <Button size="sm" variant="ghost" icon="arrow-right" onClick={() => onNavigate(href("policies", pol.id))}>
                  Open
                </Button>
              }
            >
              <Code>{pol.expression}</Code>
            </Card>
          ) : null}
        </aside>
      </div>
    </PageShell>
  );
}

const EVENT_TONE: Record<DecisionEvent["kind"], "pass" | "fail" | "unknown" | "accent" | "waived" | "neutral"> = {
  run_completed: "accent",
  finding_opened: "fail",
  assigned: "accent",
  comment: "neutral",
  request_evidence: "unknown",
  remediation_requested: "unknown",
  exception_requested: "unknown",
  exception_accepted: "waived",
  approve_remediation: "pass",
  reject_remediation: "fail",
  escalated: "fail",
  ready_for_reverification: "accent",
  reverified: "pass",
  receipt_issued: "pass",
  dismiss: "neutral",
};

const EVENT_GLYPH: Record<DecisionEvent["kind"], string> = {
  run_completed: "▸",
  finding_opened: "✕",
  assigned: "→",
  comment: "”",
  request_evidence: "?",
  remediation_requested: "!",
  exception_requested: "?",
  exception_accepted: "⦸",
  approve_remediation: "✓",
  reject_remediation: "✕",
  escalated: "↑",
  ready_for_reverification: "↻",
  reverified: "✓",
  receipt_issued: "⦿",
  dismiss: "–",
};

/**
 * The decision form.
 *
 * `request_evidence` is listed first among the options for an UNKNOWN finding and
 * last for a FAIL, because the right default differs: an UNKNOWN usually means the
 * evidence is the problem, and a FAIL usually means the control is. Ordering the
 * options by likely-correctness is a small thing that measurably changes what
 * reviewers pick.
 */
function DecisionForm({ finding }: { finding: Finding }) {
  const store = useStore();
  const [choice, setChoice] = useState<ReviewDecisionKind | null>(null);
  const [rationale, setRationale] = useState("");
  const [escalating, setEscalating] = useState(false);

  const order: ReviewDecisionKind[] =
    finding.verificationStatus === "UNKNOWN"
      ? ["request_evidence", "accept_exception", "approve_remediation", "reject_remediation", "dismiss"]
      : ["approve_remediation", "reject_remediation", "accept_exception", "request_evidence", "dismiss"];

  const submit = () => {
    if (escalating) {
      store.escalate(finding.id, rationale);
      setRationale("");
      setEscalating(false);
      return;
    }
    if (!choice) {
      store.toast("Pick a decision first.");
      return;
    }
    store.recordDecision(finding.id, choice, rationale);
    setRationale("");
    setChoice(null);
  };

  return (
    <Card
      title="Record a decision"
      subtitle="Your rationale is written into the receipt trail and is visible to any external reviewer with access to this control."
    >
      <div className="vp-form">
        <FieldGroup label="Decision" required id={`decision-${finding.id}`}>
          {order.map((k) => (
            <Choice
              key={k}
              selected={choice === k && !escalating}
              title={DECISION_LABEL[k]}
              desc={DECISION_HELP[k]}
              onSelect={() => {
                setChoice(k);
                setEscalating(false);
              }}
            />
          ))}
          <Choice
            selected={escalating}
            title="Escalate the disagreement"
            desc="You and the control owner do not agree, and you would rather not settle it alone. Hands the finding to the workspace owner with your reasoning attached. Nothing is closed."
            onSelect={() => {
              setEscalating(true);
              setChoice(null);
            }}
          />
        </FieldGroup>

        <div>
          <Label required htmlFor={`rationale-${finding.id}`}>
            Rationale
          </Label>
          <TextArea
            id={`rationale-${finding.id}`}
            value={rationale}
            onChange={setRationale}
            placeholder={
              escalating
                ? "What is the disagreement, and what would settle it?"
                : "Why is this the right outcome? Name the evidence you relied on."
            }
          />
          <p className="vp-help">
            Required. This workspace's <span className="vp-mono">silent_approve_rate</span> is 0% and that is a
            hard gate, not a target — there is no path to closing a finding without a written reason.
          </p>
        </div>

        <div className="vp-btn-row">
          <Button
            variant={choice === "approve_remediation" ? "accept" : choice === "dismiss" ? "reject" : "primary"}
            size="lg"
            icon="check"
            onClick={submit}
            disabled={!choice && !escalating}
          >
            {escalating ? "Escalate" : choice ? DECISION_LABEL[choice] : "Record decision"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setChoice(null);
              setEscalating(false);
              setRationale("");
            }}
          >
            Clear
          </Button>
          <span style={{ marginLeft: "auto" }}>
            <Select
              label="Reassign to"
              value="keep"
              onChange={(v) => {
                if (v !== "keep") store.assign(finding.id, v);
              }}
              options={[
                { value: "keep", label: `Assigned to ${finding.assignedActorId ? actor(finding.assignedActorId).name : "nobody"}` },
                ...ACTORS.filter((a) => a.role !== "system" && a.id !== finding.assignedActorId).map((a) => ({
                  value: a.id,
                  label: `Reassign to ${a.name}`,
                })),
              ]}
            />
          </span>
        </div>
      </div>
    </Card>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   RUNS
   ══════════════════════════════════════════════════════════════════════════ */

export function RunsPage({ onNavigate }: { onNavigate: (to: string) => void }) {
  const [q, setQ] = useState("");
  const [trigger, setTrigger] = useState<"all" | "manual" | "schedule" | "connector_refresh" | "api">("all");

  const rows = useMemo(() => {
    let list = RUNS;
    if (trigger !== "all") list = list.filter((r) => r.trigger === trigger);
    if (q.trim()) {
      const n = q.toLowerCase();
      list = list.filter((r) => r.label.toLowerCase().includes(n) || r.id.includes(n));
    }
    return [...list].sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [q, trigger]);

  return (
    <PageShell
      wide
      eyebrow="Decide"
        title="Runs"
        lede="Every verification run, newest first. A run is the unit that produces receipts — open one to see the whole chain from evidence to signature."
    >

      <div className="vp-filters">
        <SearchBox value={q} onChange={setQ} placeholder="Search runs…" />
        <Select
          label="Trigger"
          value={trigger}
          onChange={setTrigger}
          options={[
            { value: "all", label: "All triggers" },
            { value: "manual", label: "Manual" },
            { value: "schedule", label: "Scheduled" },
            { value: "connector_refresh", label: "Connector refresh" },
            { value: "api", label: "API" },
          ]}
        />
        <span className="vp-filter-spacer" />
        <span className="vp-result-count">{rows.length} runs</span>
      </div>

      <Card flush>
        <Table
          columns={[
            {
              key: "label",
              header: "Run",
              primary: true,
              sortValue: (r) => r.label,
              render: (r) => (
                <>
                  {r.label}
                  <div className="vp-mono" style={{ color: "var(--ink-3)", marginTop: "calc(var(--spacing) * 0.5)", fontSize: "var(--type-subtext-size)" }}>
                    {r.id} · {r.engineVersion}
                  </div>
                </>
              ),
            },
            {
              key: "verdict",
              header: "Verdict",
              sortValue: (r) => r.aggregate,
              render: (r) => <Verdict status={r.aggregate} />,
            },
            {
              key: "controls",
              header: "Controls",
              sortValue: (r) => r.controlIds.length,
              render: (r) => <span className="vp-mono">{r.controlIds.length}</span>,
            },
            {
              key: "trigger",
              header: "Trigger",
              sortValue: (r) => r.trigger,
              render: (r) => r.trigger.replace("_", " "),
            },
            {
              key: "by",
              header: "Started by",
              render: (r) => actor(r.createdByActorId).name,
            },
            {
              key: "receipt",
              header: "Receipt",
              render: (r) =>
                r.receiptId ? (
                  <span className="vp-mono">{r.receiptId}</span>
                ) : (
                  <Badge tone="unknown" glyph="?">
                    none
                  </Badge>
                ),
            },
            {
              key: "when",
              header: "Started",
              sortValue: (r) => new Date(r.startedAt).getTime(),
              render: (r) => <span className="vp-mono">{fmtRelative(r.startedAt)}</span>,
            },
          ]}
          rows={rows}
          rowKey={(r) => r.id}
          onRowClick={(r) => onNavigate(href("runs", r.id))}
          empty={<Empty title="No runs match">Clear the filters to see the full run history.</Empty>}
        />
      </Card>
    </PageShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   RUN DETAIL — the full chain
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * A run, end to end.
 *
 * This is the page the alignment PRD asked for when it said "a rejected-then-
 * accepted workflow is understandable without reading raw JSON". It reads top to
 * bottom as a narrative: what was claimed, what evidence was pinned, what each
 * check concluded and why, what the aggregate came to, and what the receipt bound.
 * Nothing here is a JSON dump; every machine field is rendered as a labelled row
 * with its meaning stated.
 */
export function RunDetailPage({ id, onNavigate }: { id: string; onNavigate: (to: string) => void }) {
  const store = useStore();
  const r = findRun(id);

  if (!r) {
    return (
      <MissingObjectPage title={`No run ${id}`}>
        <Button variant="primary" onClick={() => onNavigate(href("runs"))}>
          Back to runs
        </Button>
      </MissingObjectPage>
    );
  }

  const checks = checksFor(r.id);
  const rcp = r.receiptId ? receipt(r.receiptId) : undefined;
  const rollup = checks.reduce(
    (acc, k) => {
      acc[k.status] += 1;
      return acc;
    },
    { PASS: 0, FAIL: 0, UNKNOWN: 0, NOT_RUN: 0 } as Record<VerificationStatus, number>,
  );
  const findings = store.findings.filter((f) => f.runId === r.id);

  return (
    <PageShell
      wide
      eyebrow={`Run · ${r.trigger.replace("_", " ")}`}
        title={r.label}
        lede={
          <>
            Started {fmtDateTime(r.startedAt)} by {actor(r.createdByActorId).name}
            {r.completedAt ? (
              <>
                , completed in{" "}
                {Math.round((new Date(r.completedAt).getTime() - new Date(r.startedAt).getTime()) / 1000)}s
              </>
            ) : null}
            . Engine <span className="vp-mono">{r.engineVersion}</span>.
          </>
        }
        actions={
          <>
        <Button
              icon="refresh"
              onClick={() => store.toast(`Replaying ${r.id} from its pinned evidence — verdict re-derived unchanged.`)}
            >
              Replay
        </Button>
            {rcp ? (
              <Button variant="primary" icon="seal" onClick={() => onNavigate(href("receipts", rcp.id))}>
                Open receipt
              </Button>
            ) : null}
          </>
        }
    >

      <div className="vp-inline" style={{ marginBottom: "calc(var(--spacing) * 4.5)" }}>
        <Verdict status={r.aggregate} size="lg" />
        <Badge tone={r.status === "completed" ? "pass" : r.status === "blocked" ? "fail" : "running"} size="lg">
          {r.status}
        </Badge>
        <span className="vp-mono" style={{ color: "var(--ink-3)" }}>
          {r.id}
        </span>
      </div>

      {!rcp ? (
        <Callout tone="unknown" icon="alert" title="This run issued no receipt.">
          A receipt requires every check to carry at least one evidence artifact and at least one citation,
          under deterministic, human or imported authority. This run could not meet that, so there is nothing
          to sign — which is the correct outcome, not a bug.
        </Callout>
      ) : null}

      {checks.length > 0 ? (
        <Section title="Check results" note={`${checks.length} checks across ${r.controlIds.length} controls.`}>
          <Card flush>
            <div className="vp-card-body">
              <Meter
                segments={[
                  { tone: "pass", value: rollup.PASS, label: "PASS" },
                  { tone: "fail", value: rollup.FAIL, label: "FAIL" },
                  { tone: "unknown", value: rollup.UNKNOWN, label: "UNKNOWN" },
                  { tone: "waived", value: rollup.NOT_RUN, label: "NOT RUN" },
                ]}
              />
            </div>
            <div className="vp-rows" style={{ borderTop: "1px solid var(--line)" }}>
              {checks.map((k) => {
                const c = control(k.controlId);
                const req = requirementsFor(k.controlId).find((x) => x.id === k.requirementId);
                return (
                  <div key={k.id} className="vp-row" style={{ alignItems: "flex-start" }}>
                    <span style={{ flex: "0 0 auto", paddingTop: "calc(var(--spacing) * 0.5)" }}>
                      <Verdict status={k.status} />
                    </span>
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 2)" }}>
                          {c?.ref}
                        </span>
                        {req?.text ?? k.requirementId}
                      </span>
                      <span className="vp-row-sub">{k.reason}</span>
                      {k.citationRefs.length > 0 ? (
                        <span
                          className="vp-mono"
                          style={{ display: "block", fontSize: "var(--type-subtext-size)", marginTop: "calc(var(--spacing) * 1)", color: "var(--ink-3)" }}
                        >
                          {k.citationRefs.join("  ·  ")}
                        </span>
                      ) : null}
                    </span>
                    <span className="vp-row-aside" style={{ alignItems: "flex-end", flexDirection: "column", gap: "calc(var(--spacing) * 1)" }}>
                      <Badge tone={k.authority === "semantic" ? "waived" : "neutral"}>
                        {AUTHORITY_LABEL[k.authority]}
                      </Badge>
                      <span className="vp-tiny vp-mono">{k.durationMs} ms</span>
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        </Section>
      ) : null}

      <div className="vp-split" style={{ marginTop: "calc(var(--spacing) * 6.5)" }}>
        <div className="vp-stack">
          {findings.length > 0 ? (
            <Card
              title="Findings this run opened"
              subtitle="A PASS opens nothing. Requirements that fail for the same root cause share one finding."
              flush
            >
              <div className="vp-rows">
                {findings.map((f) => (
                  <button
                    key={f.id}
                    type="button"
                    className="vp-row"
                    data-clickable="true"
                    onClick={() => onNavigate(href("findings", f.id))}
                  >
                    <span className="vp-row-main">
                      <span className="vp-row-title">
                        <span className="vp-mono" style={{ color: "var(--ink-3)", marginRight: "calc(var(--spacing) * 2)" }}>
                          {f.ref}
                        </span>
                        {f.title}
                      </span>
                      <span className="vp-row-sub">
                        {CASE_STATUS_LABEL[f.caseStatus]} · assigned to{" "}
                        {f.assignedActorId ? actor(f.assignedActorId).name : "nobody"}
                      </span>
                    </span>
                    <span className="vp-row-aside">
                      <Severity level={f.severity} />
                      <Verdict status={f.verificationStatus} />
                    </span>
                  </button>
                ))}
              </div>
            </Card>
          ) : null}

          <Card
            title="Evidence pinned to this run"
            subtitle="Hashes recorded at run time. A later change to a source cannot alter what this run decided."
            flush
          >
            <div className="vp-rows">
              {r.evidenceArtifactIds.map((aid) => {
                const art = artifact(aid);
                if (!art) return null;
                return (
                  // A <div> with the navigation on an INNER button, not a
                  // <button> wrapping everything: the row also carries a <Hash>,
                  // which is itself a button, and `<button>` inside `<button>` is
                  // invalid HTML that React warns about and assistive tech
                  // handles unpredictably. This way the two controls are
                  // siblings — click the label to open the artifact, click the
                  // hash to copy it.
                  <div key={aid} className="vp-row">
                    <button
                      type="button"
                      className="vp-row-enter vp-row-main"
                      onClick={() => onNavigate(href("evidence", aid))}
                    >
                      <span className="vp-row-title">{art.label}</span>
                      <span className="vp-row-sub">
                        {source(art.sourceId)?.displayName} · collected {fmtDateTime(art.collectedAt)}
                      </span>
                    </button>
                    <span className="vp-row-aside">
                      <Hash value={art.contentHash} onCopy={() => store.toast("Content hash copied.")} />
                    </span>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>

        <aside className="vp-split-aside">
          <Card title="Run record">
            <Fields>
              <Field label="Run id">
                <span className="vp-mono">{r.id}</span>
              </Field>
              <Field label="Aggregate">
                <Verdict status={r.aggregate} />
              </Field>
              <Field label="Engine">
                <span className="vp-mono">{r.engineVersion}</span>
              </Field>
              <Field label="Trigger">{r.trigger.replace("_", " ")}</Field>
              <Field label="Started">{fmtDateTime(r.startedAt)}</Field>
              <Field label="Completed">{r.completedAt ? fmtDateTime(r.completedAt) : "—"}</Field>
              <Field label="Controls">{r.controlIds.length}</Field>
              <Field label="Evidence">{r.evidenceArtifactIds.length} artifacts</Field>
            </Fields>
          </Card>

          {rcp ? (
            <Card
              title="Receipt"
              subtitle={`Signed ${fmtDateTime(rcp.issuedAt)}`}
              actions={
                <Button size="sm" variant="ghost" icon="arrow-right" onClick={() => onNavigate(href("receipts", rcp.id))}>
                  Open
                </Button>
              }
            >
              <Fields>
                <Field label="Receipt id">
                  <span className="vp-mono">{rcp.id}</span>
                </Field>
                <Field label="Status">
                  <Verdict status={rcp.status} />
                </Field>
                <Field label="Payload hash">
                  <Hash value={rcp.payloadHash} onCopy={() => store.toast("Payload hash copied.")} />
                </Field>
                <Field label="Signing key">
                  <span className="vp-mono">{rcp.signatureKeyId}</span>
                </Field>
                <Field label="Algorithm">
                  <span className="vp-mono">{rcp.signatureAlgorithm}</span>
                </Field>
                <Field label="Offline replay">
                  {rcp.replayVerified ? (
                    <Badge tone="pass" glyph="✓">
                      verified {rcp.replayCheckedAt ? fmtRelative(rcp.replayCheckedAt) : ""}
                    </Badge>
                  ) : (
                    <Badge tone="unknown" glyph="?">
                      not checked
                    </Badge>
                  )}
                </Field>
              </Fields>
            </Card>
          ) : null}

          <Card title="Policy versions used">
            <div className="vp-stack" data-gap="sm">
              {(rcp?.policyVersionRefs ?? []).map((pid) => {
                const p = policy(pid);
                return (
                  <button
                    key={pid}
                    type="button"
                    className="vp-btn"
                    data-variant="outline"
                    data-size="sm"
                    onClick={() => onNavigate(href("policies", pid))}
                  >
                    <span className="vp-mono">{pid}</span>
                    {p ? ` · v${p.version}` : ""}
                  </button>
                );
              })}
              {(rcp?.policyVersionRefs ?? []).length === 0 ? (
                <p className="vp-tiny" style={{ margin: "0" }}>
                  No receipt, so no policy versions were bound.
                </p>
              ) : null}
            </div>
          </Card>
        </aside>
      </div>
    </PageShell>
  );
}
